import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { H1, Alert, Loader } from '../components';
import { colors, isStringEmpty, formatTimespan } from '../utils';
import { usePolling } from '../lib/hooks';
import { getAll as jobGetAllAction, gotoJob as gotoJobAction } from '../lib/redux/actions/job';
import isLoadingSelector from '../lib/redux/selectors/app/is-loading';
import ChevronBlue from '../assets/images/chevron-blue.svg';
import { getPhoneNumber } from '../lib/redux/selectors/me';
import EditPhoneNumberModal from '../components/EditPhoneNumberModal';
import AgentMetrics from '../components/AgentMetrics';
import DurationFilter from '../components/DurationFilter';

const ChevronIconForward = styled.img`
  height: 40px;
  width: 40px;
`;

const HomeWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;

  table {
    width: 827px;
    margin-bottom: 30px;
    border-collapse: collapse;

    tr {
      td {
        background-color: white;
      }

      td:first-child {
        padding-left: 10px;
      }

      td:last-child {
        padding-right: 10px;
      }

      td:first-child {
        border-top-left-radius: 4px;
      }
      td:last-child {
        border-top-right-radius: 4px;
      }
      td:first-child {
        border-bottom-left-radius: 4px;
      }
      td:last-child {
        border-bottom-right-radius: 4px;
      }
    }

    thead {
      font-weight: bold;

      tr {
        height: 40px;

        td {
          font-size: 16px;
          font-family: Mada;
          color: #4d4d4d;
        }

        td:last-child {
          text-align: right;
        }

        td.job-name {
          width: 45%;
        }
      }
    }

    tbody {
      tr {
        border-top: 3px solid ${colors.g01};
        height: 80px;
        transition: all 0.15s ease;

        td {
          &:last-child {
            color: #00acca;
            text-align: right;
          }
        }

        &:hover {
          box-shadow: 3px 2px 4px #0000001a;
          transform: scale(1, 1); // Force box-shadow to render correctly
          cursor: pointer;
        }
      }
    }
  }
`;

const LoaderWrapper = styled.div`
  margin-top: 160px;
`;

const Title = styled(H1)`
  margin-top: 30px;
  margin-bottom: 30px;
  font-weight: bold;
`;

const Dashboard = ({
  jobList,
  jobActivate,
  dispatchJobGetAll,
  history,
  gotoJob,
  isLoading,
  phoneNumber
}) => {
  usePolling(dispatchJobGetAll);
  const [showEditPhoneNumber, setShowEditPhoneNumber] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const handleJobClicked = job => {
    setSelectedJob(job);
    if (isStringEmpty(phoneNumber)) {
      setShowEditPhoneNumber(true);
      return;
    }
    gotoJob(job, history);
  };

  if (isLoading) {
    return (
      <HomeWrapper>
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      </HomeWrapper>
    );
  }

  return (
    <>
      {jobList.error && (
        <Alert error danger fontWeight="bold">
          Failed to load jobs. Please try again.
        </Alert>
      )}
      {jobActivate.error && (
        <Alert error danger fontWeight="bold">
          Failed to activate job. {jobActivate.error.body.reason}
        </Alert>
      )}
      <HomeWrapper>
        <Title>Dashboard</Title>
        {window._env_.AGENT_METRICS_ENABLED === 'true' && (
          <div>
            <DurationFilter />
            <AgentMetrics />
          </div>
        )}
        <table>
          <thead>
            <tr>
              <td className="job-name">Job name</td>
              <td>Customers</td>
              <td>Agents</td>
              {window._env_.AGENT_METRICS_ENABLED === 'true' && <td>My Recording Time</td>}
              <td>Enter job</td>
            </tr>
          </thead>
          <tbody>
            {jobList.items.map(job => (
              <tr key={job.jobId} onClick={() => handleJobClicked(job)}>
                <td>{job.title || job.jobId}</td>
                <td>{job.activeTasks}</td>
                <td>{job.agentsAvailable}</td>
                {window._env_.AGENT_METRICS_ENABLED === 'true' && (
                  <td>{formatTimespan(job.recordingTime)}</td>
                )}
                <td>
                  <ChevronIconForward src={ChevronBlue} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {jobList.loading && <Loader />}
      </HomeWrapper>
      {showEditPhoneNumber && (
        <EditPhoneNumberModal
          onSaveCallback={() => gotoJob(selectedJob, history)}
          closeModal={() => setShowEditPhoneNumber(false)}
          phoneNumber={phoneNumber}
        />
      )}
    </>
  );
};

Dashboard.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  phoneNumber: PropTypes.string,
  jobList: PropTypes.shape({
    items: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.any
  }).isRequired,
  jobActivate: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    error: PropTypes.any
  }).isRequired,
  dispatchJobGetAll: PropTypes.func.isRequired,
  gotoJob: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  metrics: PropTypes.shape({
    callCount: PropTypes.number,
    activeTime: PropTypes.string,
    recordingTime: PropTypes.string
  }).isRequired
};

Dashboard.defaultProps = {
  phoneNumber: null
};

export default compose(
  withRouter,
  connect(
    state => ({
      isLoading: isLoadingSelector(state),
      jobList: state.job.list,
      jobActivate: state.job.activate,
      phoneNumber: getPhoneNumber(state.me),
      metrics: state.me.metrics
    }),
    {
      dispatchJobGetAll: jobGetAllAction,
      gotoJob: gotoJobAction
    }
  )
)(Dashboard);
